import { TaskHealth, TaskHealthStatus } from "./plannerUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle, AlertTriangle, AlertCircle, Clock, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface TaskHealthIndicatorProps {
  health: TaskHealth;
  size?: "sm" | "md";
}

const statusConfig: Record<TaskHealthStatus, {
  icon: typeof CheckCircle;
  bgClass: string;
  textClass: string;
  borderClass: string;
  label: string;
}> = {
  completed: {
    icon: CheckCircle,
    bgClass: "bg-muted",
    textClass: "text-muted-foreground",
    borderClass: "border-muted-foreground/30",
    label: "Completed",
  },
  on_track: {
    icon: TrendingUp,
    bgClass: "bg-green-500/10",
    textClass: "text-green-600 dark:text-green-400",
    borderClass: "border-green-500/30",
    label: "On Track",
  },
  at_risk: {
    icon: Clock,
    bgClass: "bg-yellow-500/10",
    textClass: "text-yellow-600 dark:text-yellow-400",
    borderClass: "border-yellow-500/30",
    label: "At Risk",
  },
  critical: {
    icon: AlertTriangle,
    bgClass: "bg-red-500/10",
    textClass: "text-red-600 dark:text-red-400",
    borderClass: "border-red-500/30",
    label: "Critical",
  },
  overdue: {
    icon: AlertCircle,
    bgClass: "bg-red-700/10",
    textClass: "text-red-700 dark:text-red-500",
    borderClass: "border-red-700/30",
    label: "Overdue",
  },
};

export function TaskHealthIndicator({ health, size = "sm" }: TaskHealthIndicatorProps) {
  const config = statusConfig[health.status];
  const Icon = config.icon;

  const iconSize = size === "sm" ? "h-3 w-3" : "h-4 w-4";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5",
              config.bgClass,
              config.textClass,
              config.borderClass
            )}
          >
            <Icon className={iconSize} />
            {size === "md" && (
              <span className="text-xs font-medium">{config.label}</span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">{config.label}</p>
            <p className="text-xs text-muted-foreground">{health.message}</p>
            {health.status !== 'completed' && (
              <div className="text-xs space-y-0.5 pt-1 border-t">
                <p>Remaining: {health.remainingHours.toFixed(1)}h</p>
                <p>Available: {health.availableHours.toFixed(1)}h</p>
                {health.overlappingHoursFromOtherTasks > 0 && (
                  <p className="text-muted-foreground">
                    Other tasks: {health.overlappingHoursFromOtherTasks.toFixed(1)}h
                  </p>
                )}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
